import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ResourceSelectFields from 'components/IAP/ResourceSelectFields';
import { useAors } from 'hooks/useAors';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  useCreateResourceSet,
  CreateResourceSetFormData,
} from 'components/IAP/hooks/resourceSetHooks';
import FormTextField from 'components/IAP/formFields/FormTextField';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import { GroupRosterItem } from 'hooks/incident/useRoster';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { type UscgSymbol } from '../LayersTab/utils/uscgSymbology';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import FormSymbologySelect from '../LayersTab/FormSymbologySelect';
import { usePrimaryPoint } from '../LayersTab/utils/usePrimaryPoint';

const AddResourceCard: React.FC<{
  setShowAddResourceCard: (show: boolean) => void;
  groupMembers: GroupRosterItem[];
  compact?: boolean;
}> = ({ setShowAddResourceCard, groupMembers, compact }) => {
  const createResourceSet = useCreateResourceSet();
  const { startDrawing, stopDrawing } = useUscgMapContext();
  const [dateTimeOrderedTimezone, setDateTimeOrderedTimezone] = useState(0);
  const [etaTimezone, setEtaTimezone] = useState(0);

  const form = useForm({
    mode: 'onChange',
  });

  const { data: aors = [] } = useAors();
  const { data: incidents = [] } = useAllIncidents();

  const handleCreateSet = (data: CreateResourceSetFormData) => {
    const safeFormData: CreateResourceSetFormData = {
      ...data,
      name: data.name ?? '',
    } as CreateResourceSetFormData;
    createResourceSet.mutate(safeFormData);
    setShowAddResourceCard(false);
  };

  const primaryPoint = usePrimaryPoint();

  useEffect(() => {
    if (primaryPoint) {
      form.setValue('latitude', String(primaryPoint.latitude));
      form.setValue('longitude', String(primaryPoint.longitude));
    }
  }, [primaryPoint, form]);

  useEffect(() => {
    return () => {
      stopDrawing();
    };
  }, [stopDrawing]);

  const handleSymbologyChange = (type: string | null) => {
    if (type) {
      startDrawing(type as UscgSymbol);
    } else {
      stopDrawing();
    }
    form.setValue('latitude', '');
    form.setValue('longitude', '');
  };

  const handleCancel = () => {
    stopDrawing();
    setShowAddResourceCard(false);
  };

  const onSubmit = async (formData: Record<string, unknown>) => {
    const { incidentId, latitude, longitude, ...resourceSetFormData } =
      formData as Record<string, unknown> & {
        incidentId?: string;
        latitude?: string;
        longitude?: string;
      };

    const lat = Number(latitude);
    const lng = Number(longitude);
    const geometry =
      latitude && longitude && !isNaN(lat) && !isNaN(lng)
        ? {
            type: 'Point' as const,
            coordinates: [lng, lat] as [number, number],
          }
        : undefined;

    handleCreateSet({
      ...resourceSetFormData,
      geometry,
      datetimeOrderedOffset: dateTimeOrderedTimezone,
      etaTimezoneOffset: etaTimezone,
      status: 'In Organization',
      incidents: incidentId ? [incidentId] : [],
    } as CreateResourceSetFormData);
  };

  if (compact) {
    return (
      <div
        className="resource-border"
        style={{
          border: '1px solid var(--brand-primary)',
        }}
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="resource-card-form-header">
              <span className="fw-semibold">Add Resource</span>
              <StylishNewButton
                type="button"
                className="button--icon"
                aria-label="Close add resource form"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                <SharedIcon iconName="cancel" size="sm" />
              </StylishNewButton>
            </div>
            <div className="resource-card-content p-4 d-flex flex-column">
              <FormTextField
                title="Resource Name"
                fieldName="name"
                rules={{
                  required: {
                    value: true,
                    message: 'Resource Name is required',
                  },
                }}
              />
              <ResourceSelectFields labels disableKind={'Personnel'} />
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormSelectField
                    title="AOR"
                    fieldName="aor"
                    options={aors.map((aor) => ({
                      value: aor.id,
                      label: aor.name,
                    }))}
                  />
                </div>
                <div className="w-50">
                  <FormSelectField
                    title="Incident"
                    fieldName="incidentId"
                    options={incidents.map((incident) => ({
                      value: incident.id,
                      label: incident.name,
                    }))}
                    isClearable
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormSelectField
                    title="OPCON"
                    fieldName="opcon"
                    options={aors.map((aor) => ({
                      value: aor.id,
                      label: aor.name,
                    }))}
                    isClearable
                  />
                </div>
                <div className="w-50">
                  <FormSelectField
                    title="TACON"
                    fieldName="tacon"
                    options={aors.map((aor) => ({
                      value: aor.id,
                      label: aor.name,
                    }))}
                    isClearable
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormSelectField
                    title="Asset Status"
                    fieldName="assetStatus"
                    options={ASSET_STATUS_FILTER_OPTIONS.map((status) => ({
                      value: status,
                      label: status,
                    }))}
                    isClearable
                  />
                </div>
                <div className="w-50">
                  <FormTextField title="Current Location" fieldName="address" />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormDateTimeField
                    title="Datetime Ordered"
                    fieldName="dateTimeOrdered"
                    timeZone
                    timeZoneLabel
                    timeZoneValue={dateTimeOrderedTimezone}
                    onTimeZoneChange={(option) =>
                      setDateTimeOrderedTimezone(option.value)
                    }
                  />
                </div>
                <div className="w-50">
                  <FormSelectField
                    title="Point of Contact"
                    fieldName="pointOfContact"
                    options={groupMembers.map((member) => ({
                      label: member.email_address,
                      value: member.user_guid,
                    }))}
                    isClearable={true}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormTextField
                    title="Owning Organization"
                    fieldName="owningOrganization"
                  />
                </div>
                <div className="w-50">
                  <FormTextField
                    title="Hull/Tail Number"
                    fieldName="hullTailNumber"
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormTextField
                    title="Quantity"
                    fieldName="quantity"
                    rules={{
                      required: {
                        value: true,
                        message: 'Quantity is required',
                      },
                    }}
                  />
                </div>
                <div className="w-50">
                  <FormDateTimeField
                    title="Estimated Time of Arrival"
                    fieldName="eta"
                    timeZone
                    timeZoneLabel
                    timeZoneValue={etaTimezone}
                    onTimeZoneChange={(option) => setEtaTimezone(option.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="w-50">
                  <FormTextField title="Unit" fieldName="unit" />
                </div>
                <div className="w-50">
                  <FormTextField title="Capabilities" fieldName="description" />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-start">
                <div className="col-4">
                  <FormSymbologySelect
                    excludeOptions={[
                      'completedBoom',
                      'proposedBoom',
                      'oilSpill',
                    ]}
                    onSymbologyChange={handleSymbologyChange}
                  />
                </div>
                <div className="col-2">
                  <FormTextField title="Latitude" fieldName="latitude" />
                </div>
                <div className="col-2">
                  <FormTextField title="Longitude" fieldName="longitude" />
                </div>
              </div>
              <FormTextAreaField title="Notes" fieldName="notes" />
              <div className="d-flex gap-2">
                <StylishNewButton type="submit">Save</StylishNewButton>
                <StylishNewButton
                  type="button"
                  className="button--secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </StylishNewButton>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    );
  }

  return (
    <div
      className="resource-border"
      style={{
        border: '1px solid var(--brand-primary)',
      }}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="resource-card-form-header">
            <span className="fw-semibold">Add Resource</span>
            <StylishNewButton
              type="button"
              className="button--icon"
              aria-label="Close add resource form"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              <SharedIcon iconName="cancel" size="sm" />
            </StylishNewButton>
          </div>
          <div className="resource-card-content p-4">
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-xl-4 col-lg-6">
                <FormTextField
                  title="Resource Name"
                  fieldName="name"
                  rules={{
                    required: {
                      value: true,
                      message: 'Resource Name is required',
                    },
                  }}
                />
              </div>
              <div className="col-xl-4 col-lg-6 mt-3">
                <ResourceSelectFields labels disableKind={'Personnel'} />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormSelectField
                  title="AOR"
                  fieldName="aor"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormSelectField
                  title="Incident"
                  fieldName="incidentId"
                  options={incidents.map((incident) => ({
                    value: incident.id,
                    label: incident.name,
                  }))}
                  isClearable
                />
              </div>
            </div>
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-xl-2 col-md-6">
                <FormSelectField
                  title="OPCON"
                  fieldName="opcon"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                  isClearable
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormSelectField
                  title="TACON"
                  fieldName="tacon"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                  isClearable
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormSelectField
                  title="Asset Status"
                  fieldName="assetStatus"
                  options={ASSET_STATUS_FILTER_OPTIONS.map((status) => ({
                    value: status,
                    label: status,
                  }))}
                  isClearable
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <FormTextField title="Current Location" fieldName="address" />
              </div>
              <div className="col-xl-3 col-md-6">
                <FormDateTimeField
                  title="Datetime Ordered"
                  fieldName="dateTimeOrdered"
                  timeZone
                  timeZoneLabel
                  timeZoneValue={dateTimeOrderedTimezone}
                  onTimeZoneChange={(option) =>
                    setDateTimeOrderedTimezone(option.value)
                  }
                />
              </div>
            </div>
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-xl-3 col-md-6">
                <FormSelectField
                  title="Point of Contact"
                  fieldName="pointOfContact"
                  options={groupMembers.map((member) => ({
                    label: member.email_address,
                    value: member.user_guid,
                  }))}
                  isClearable={true}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <FormTextField
                  title="Owning Organization"
                  fieldName="owningOrganization"
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormTextField
                  title="Hull/Tail Number"
                  fieldName="hullTailNumber"
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormTextField
                  title="Quantity"
                  fieldName="quantity"
                  rules={{
                    required: {
                      value: true,
                      message: 'Quantity is required',
                    },
                  }}
                />
              </div>
              <div className="col-xl-2 col-md-6">
                <FormTextField title="Unit" fieldName="unit" />
              </div>
            </div>
            <div className="resource-card-section row gx-1 gy-2 align-items-start">
              <div className="col-xl-3 col-md-6">
                <FormDateTimeField
                  title="Estimated Time of Arrival"
                  fieldName="eta"
                  timeZone
                  timeZoneLabel
                  timeZoneValue={etaTimezone}
                  onTimeZoneChange={(option) => setEtaTimezone(option.value)}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <FormTextField title="Capabilities" fieldName="description" />
              </div>
              <div className="col-xl-2 col-lg-3">
                <FormSymbologySelect
                  excludeOptions={['completedBoom', 'proposedBoom', 'oilSpill']}
                  onSymbologyChange={handleSymbologyChange}
                />
              </div>
              <div className="col-xl-2 col-md-3">
                <FormTextField title="Latitude" fieldName="latitude" />
              </div>
              <div className="col-xl-2 col-md-3">
                <FormTextField title="Longitude" fieldName="longitude" />
              </div>
            </div>
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-12">
                <FormTextAreaField title="Notes" fieldName="notes" />
              </div>
            </div>
            <div className="d-flex gap-2 mt-4">
              <StylishNewButton type="submit">Save</StylishNewButton>
              <StylishNewButton
                type="button"
                className="button--secondary"
                onClick={handleCancel}
              >
                Cancel
              </StylishNewButton>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddResourceCard;
